import React, { useEffect, useState, useCallback, useRef } from 'react';
import {
  Modal,
  View,
  TouchableOpacity,
  Image,
  ScrollView,
  Text,
  NativeSyntheticEvent,
  NativeScrollEvent,
} from 'react-native';
import closeIcon from 'src/assets/close.png';
import Button from 'src/components/Button/Button';
import Typography from 'src/components/Text/Text';
import { styles } from './YearSelectionModal.styles';

interface YearSelectionModalProps {
  isVisible: boolean;
  onClose: () => void;
  onSelectYear: (year: number) => void;
  testID?: string;
}

const YearSelectionModal: React.FC<YearSelectionModalProps> = ({
  isVisible,
  onClose,
  onSelectYear,
  testID,
}) => {
  const [selectedYear, setSelectedYear] = useState<number | null>(null);
  const scrollRef = useRef<ScrollView | null>(null);
  const itemHeight = 50; // Height of each year item
  const visibleItems = 5; // Number of items visible

  const years = Array.from({ length: 16 }, (_, i) => 2010 + i);

  // Calculate the offset for the middle year
  const middleIndex = Math.floor(visibleItems / 2);

  useEffect(() => {
    if (isVisible && scrollRef.current) {
      // Scroll to the middle year when the modal opens
      const initialIndex = selectedYear ? years.indexOf(selectedYear) : middleIndex;
      scrollRef.current.scrollTo({
        y: initialIndex * itemHeight,
        animated: true,
      });
    }
  }, [isVisible]);

  const handleScroll = (event: NativeSyntheticEvent<NativeScrollEvent>) => {
    const offsetY = event.nativeEvent.contentOffset.y;
    const index = Math.round(offsetY / itemHeight); // Determine the year in the middle
    setSelectedYear(years[index]);
  };

  const handleConfirm = useCallback(() => {
    if (selectedYear !== null) {
      onSelectYear(selectedYear);
      onClose();
    }
  }, [selectedYear, onClose, onSelectYear]);

  return (
    <Modal visible={isVisible} transparent animationType="slide">
      <View style={styles.modalOverlay}>
        <View style={styles.modalContainer}>
          <View style={styles.header}>
            <Typography variant="h2">In which year did the loss occur?</Typography>
            <TouchableOpacity onPress={onClose} style={styles.closeIcon}>
              <Image
                source={closeIcon}
                style={styles.closeImage}
                accessibilityLabel={`${testID}-close-icon`}
              />
            </TouchableOpacity>
          </View>

          {/* Year Selector */}
          <View style={styles.scrollContainer}>
            <ScrollView
              testID={testID}
              ref={scrollRef}
              showsVerticalScrollIndicator={false}
              snapToInterval={itemHeight} // Snap to each year
              decelerationRate="fast"
              onScroll={handleScroll}
              scrollEventThrottle={16}>
              <View style={{ paddingVertical: (itemHeight * (visibleItems - 1)) / 2 }}>
                {years.map((year) => (
                  <View
                    key={year}
                    style={styles.yearItem}
                    testID={`${testID}-${year}-${selectedYear === year}`}>
                    <Text
                      style={[
                        styles.yearText,
                        selectedYear === year && styles.highlightedYearText,
                      ]}>
                      {year}
                    </Text>
                  </View>
                ))}
              </View>
            </ScrollView>
            {/* Overlay for fixed background */}
            <View style={styles.fixedHighlight} />
          </View>

          <View style={styles.buttonContainer}>
            <View style={styles.buttonWrapper1}>
              <Button
                type="tertiary"
                title="Cancel"
                onPress={onClose}
                testID={`${testID}-cancel`}
              />
            </View>
            <View style={styles.buttonWrapper2}>
              <Button
                testID={`${testID}-confirm`}
                type="primary"
                title="Confirm"
                onPress={handleConfirm}
                disabled={selectedYear === null}
              />
            </View>
          </View>
        </View>
      </View>
    </Modal>
  );
};

export default YearSelectionModal;
